/**
 * venture/regdate.ts — 등록 서류 날짜 자동 점검 (2026-10-22 지도사 「날짜 체크 해야겠다 자동으로」)
 *  엔젤투자협회 보완 요청(261009): 「의사록 — 총회 개최 후 14일 이내 등록 신청해야 합니다. 날짜를 바르게 수정해 주시기 바랍니다.」
 *  지도사: 「LP 정보 동의서도 너무 오래전 날짜라고 반려 당해 봤다」
 *  원인: 생성 서류 전체가 신청일(작성일) 하나(master.applyDate · 결성계획 때 날짜)를 썼다 → 몇 달 뒤 등록 서류도 그 날짜로 나옴.
 *  고침: 7단계 등록 서류는 「결성총회일」(master.regApplyDate)로 따로 만든다 · 비면 예전처럼 신청일.
 *   점검 ① 총회일 + 14일 = 등록 신청 기한 — 지났으면 빨강 · 3일 안이면 주의 ② 등록 서류가 다른 날짜로 만들어져 있으면 「다시 만들기」.
 */
import type { DocVer, Master, Project } from './types';

/** 7단계 등록 서류(결성총회일 날짜로 만드는 것) — Step7 「마스터값으로 7단계 문서 재생성」과 같은 목록 */
export const REG_KEYS = ['admininfo', 'regform', 'minutes', 'roster', 'jeungpyo', 'lpprivacy'] as const;
export const REG_LIMIT = 14;   // 총회 개최 후 14일 이내 등록 신청(엔젤투자협회)
export const REG_SOON = 3;     // 기한이 이만큼 남으면 주의

const ISO = /^\d{4}-\d{2}-\d{2}$/;
const iso = (v: unknown): string => { const s = String(v || '').trim().slice(0, 10); return ISO.test(s) ? s : ''; };
const addDays = (d: string, n: number): string => new Date(Date.parse(d + 'T00:00:00Z') + n * 86400000).toISOString().slice(0, 10);
const diff = (a: string, b: string): number => Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000);
export const md = (d: string): string => { const m = d.match(/^\d{4}-(\d{2})-(\d{2})/); return m ? `${+m[1]}/${+m[2]}` : ''; };
export function todayKst(now: number = Date.now()): string { return new Date(now + 9 * 3600 * 1000).toISOString().slice(0, 10); }

/** 등록 서류 날짜 = 결성총회일 · 비면 신청일(작성일) */
export function regDateOf(m: Master | null | undefined): string {
  if (!m) return '';
  return iso(m.regApplyDate) || iso(m.applyDate);
}
/** 이 버전이 어떤 날짜로 만들어졌나 — 날짜를 적기 전(2026-10-22 전) 버전은 신청일로 만든 것 */
export function verDate(v: DocVer | undefined, m: Master | null | undefined): string {
  if (!v) return '';
  return iso(v.date) || (m ? iso(m.applyDate) : '');
}

export type RegLevel = 'none' | 'future' | 'ok' | 'soon' | 'late';
export interface RegDateCheck {
  date: string;          // 결성총회일(등록 서류 날짜)
  deadline: string;      // 등록 신청 기한(총회일 + 14일)
  daysLeft: number;      // 오늘부터 기한까지(음수면 지남)
  level: RegLevel;
  text: string;          // 한 줄 안내
  stale: string[];       // 다른 날짜로 만들어진 등록 서류 키
  staleSealed: string[]; // 그중 날인본(다시 날인 받아야 함)
}

/** 등록 서류 날짜 점검 — today 는 한국 날짜(YYYY-MM-DD) */
export function checkRegDate(p: Project, today: string = todayKst()): RegDateCheck {
  const m = p.master;
  const date = regDateOf(m);
  const none: RegDateCheck = { date: '', deadline: '', daysLeft: 0, level: 'none', text: '결성총회일을 넣어 주세요', stale: [], staleSealed: [] };
  if (!date) return none;
  const deadline = addDays(date, REG_LIMIT);
  const daysLeft = diff(today, deadline);
  const level: RegLevel = date > today ? 'future' : daysLeft < 0 ? 'late' : daysLeft <= REG_SOON ? 'soon' : 'ok';
  const text = level === 'future' ? `총회 예정 ${md(date)} · 등록 신청은 ${md(date)} ~ ${md(deadline)}`
    : level === 'late' ? `결성총회 ${md(date)} · 등록 신청 기한(${md(deadline)})에서 ${-daysLeft}일 지났어요`
      : `결성총회 ${md(date)} · 등록 신청 기한 ${md(deadline)}${daysLeft === 0 ? ' (오늘까지)' : ` · ${daysLeft}일 남음`}`;
  const stale: string[] = []; const staleSealed: string[] = [];
  REG_KEYS.forEach((k) => {
    const list = (p.docver && p.docver[k]) || [];
    const last = list[list.length - 1];
    if (!last || !(last.s3doc || last.s3pdf)) return;   // 아직 안 만든 서류는 만들 때 새 날짜로 나온다
    if (verDate(last, m) !== date) { stale.push(k); if (last.kind !== 'auto') staleSealed.push(k); }
  });
  return { date, deadline, daysLeft, level, text, stale, staleSealed };
}
